import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetLadderQuery } from '@/services/qduelsApi';
import { useResolveProfilesBySubsQuery } from '@/services/socialApi';
import { getMyLogtoSub } from '@/lib/logto';
import type { QduelsUserLeague } from '@/types/qduels';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatNumero } from '@/lib/fmt';

/**
 * One QDuels tournament ladder, with real names.
 *
 * The rows the server sends carry ONLY the Logto `sub` — a ranking of UUIDs
 * isn't a product, so names are resolved through QSocials, the same way
 * `/league` already does it. A sub QSocials can't resolve falls back to its
 * rank ("Giocatore #4"), never to a raw UUID.
 */
export function TournamentLadder({ league }: { league: QduelsUserLeague }) {
  const { t } = useTranslation('leaderboard');
  const lingua = useLinguaFormato();
  const mySub = getMyLogtoSub();

  const { data, isLoading, isError } = useGetLadderQuery({
    platform: league.platform,
    guildId: league.guildId,
    month: league.seasonMonth,
    limit: 50,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  // Sorted for a stable cache key — the same subs in a different order would
  // otherwise be a cache miss on every render.
  const subs = useMemo(() => [...new Set(rows.map((r) => r.sub))].sort(), [rows]);
  const profilesQ = useResolveProfilesBySubsQuery({ subs }, { skip: subs.length === 0 });
  const nameBySub = useMemo(() => {
    const m = new Map<string, { displayName?: string | null; avatarUrl?: string | null }>();
    for (const p of profilesQ.data ?? []) m.set(p.sub, p);
    return m;
  }, [profilesQ.data]);

  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center">
        <Loader />
      </div>
    );
  }
  // A 404 here means "no league for this server-month yet" — a normal state, not
  // a failure. Either way there's nothing to rank, so the section stands down.
  if (isError || rows.length === 0) return null;

  return (
    <section className="flex flex-col gap-2 pt-1">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-brand">
          {league.platform === 'global'
            ? t('ladder.global', { defaultValue: 'Tournaments — global' })
            : t('ladder.server', { defaultValue: 'Tournaments — your server' })}
        </h3>
        {/* The caller's own standing, echoed by the server so we can say "sei #N"
            even when they're outside the fetched page. Typed since day one and
            never rendered until now. */}
        {data?.caller && (
          <span className="text-xs text-fg-muted">
            {t('ladder.you_are', { defaultValue: "you're #{{rank}}", rank: data.caller.rank })}
          </span>
        )}
      </div>

      <ul className="flex flex-col gap-1.5">
        {rows.map((r) => {
          const prof = nameBySub.get(r.sub);
          const isMe = !!mySub && r.sub === mySub;
          const name =
            prof?.displayName ||
            `${t('ladder.player', { defaultValue: 'Player' })} #${r.rank}`;
          return (
            <li
              key={r.sub}
              className={cn(
                'flex items-center gap-3 rounded-xl px-2.5 py-2',
                isMe ? 'bg-brand/15 ring-1 ring-brand/40' : 'bg-bg-secondary',
              )}
            >
              <span
                className={cn(
                  'w-6 shrink-0 text-center text-xs font-bold tabular-nums',
                  r.rank === 1 ? 'text-brand' : 'text-fg-muted',
                )}
              >
                {r.rank}
              </span>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary text-[10px] font-bold text-fg">
                {prof?.avatarUrl ? (
                  <img src={prof.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  (name.charAt(0) || '?').toLocaleUpperCase(lingua)
                )}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
                {name}
                {isMe && (
                  <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-brand">
                    {t('ladder.you', { defaultValue: 'You' })}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-sm font-bold tabular-nums text-fg">
                {formatNumero(Math.round(r.score), lingua)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
